/**
 * TextLabelFilterStep — step 2 of the ABR filter form dialog.
 * Allows selecting text labels with severity thresholds and an AND/OR modifier.
 */

import { LabelSelector, type Label, type SeverityConfig, type SeverityLabelOption, getSeverityLabel } from '@core/components/LabelSelector';
import type { LabelFilterInput, FilterOperator } from '@moderation/domain/AbrFilter';
import { Heading, Select, Text } from '@soteria/design-system-ve';
import { ContentType, type StandardLabel } from '@system-config/label-management/domain';
import { useCallback, useMemo } from 'react';

import * as styles from './AbrFilterFormDialog.css';
import { MODIFIER_OPTIONS, isFilterOperator } from './abrFilterConstants';
import { labelsToTextFilters, textFiltersToLabels } from './labelFilterConversions';

export interface TextLabelFilterStepProps {
  textLabelFilters: LabelFilterInput[];
  textLabelFilterModifier: FilterOperator;
  standardLabels: StandardLabel[];
  labelsLoading: boolean;
  onTextLabelFiltersChange: (filters: LabelFilterInput[]) => void;
  onTextLabelFilterModifierChange: (modifier: FilterOperator) => void;
}

const SEVERITY_CONFIG: SeverityConfig = {
  mode: 'severity',
  min: 1,
  defaultValue: 2,
  defaultMax: 3,
  showDescription: true,
  getValueLabel: getSeverityLabel,
};

export function TextLabelFilterStep({
  textLabelFilters,
  textLabelFilterModifier,
  standardLabels,
  labelsLoading,
  onTextLabelFiltersChange,
  onTextLabelFilterModifierChange,
}: TextLabelFilterStepProps) {
  const textLabelOptions: SeverityLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.Text)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
          maxSeverity: label.maxSeverity ?? undefined,
        })),
    [standardLabels]
  );

  const textLabelsForSelector = useMemo(() => textFiltersToLabels(textLabelFilters), [textLabelFilters]);

  const handleTextLabelsChange = useCallback((labels: Label[]) => onTextLabelFiltersChange(labelsToTextFilters(labels)), [onTextLabelFiltersChange]);

  return (
    <div className={styles.stepContentStyles}>
      <div className={styles.fieldGroupStyles}>
        <Heading level="h3">Text Label Filters</Heading>
        <Text className={styles.toggleDescriptionStyles}>
          Match cases with text labels at or above the severity threshold. Cases are matched using the selected modifier (AND/OR).
        </Text>
      </div>

      <div className={styles.fieldGroupStyles}>
        <Text className={styles.fieldLabelStyles}>Modifier</Text>
        <div className={styles.modifierRowStyles}>
          <Select
            aria-label="Text label filter modifier"
            options={MODIFIER_OPTIONS}
            value={textLabelFilterModifier}
            onChange={v => {
              if (isFilterOperator(v)) onTextLabelFilterModifierChange(v);
            }}
          />
        </div>
      </div>

      <div className={styles.fieldGroupStyles}>
        {textLabelOptions.length > 0 ? (
          <LabelSelector
            enableBulkSelection
            availableLabels={textLabelOptions}
            config={SEVERITY_CONFIG}
            emptyStateMessage="No text labels selected. Add labels to filter by text content severity."
            loading={labelsLoading}
            selectedLabels={textLabelsForSelector}
            title="Text Labels"
            onChange={handleTextLabelsChange}
          />
        ) : (
          <div aria-live="polite" role="status">
            {labelsLoading ? <Text>Loading text labels...</Text> : <Text>No text labels available. Please configure labels in system settings.</Text>}
          </div>
        )}
      </div>
    </div>
  );
}
